// src/pages/seller/EditProductPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../utils/firebase';
import { normalizeAndValidateTiers } from '../../utils/moqUtils';
// import { categoryStructure } from '../../components/Header';

const EditProductPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [newImages, setNewImages] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const docSnap = await getDoc(doc(db, 'products', id));
        if (docSnap.exists()) {
          const data = docSnap.data();
          setProduct({
            id: docSnap.id,
            ...data,
            sku: data.sku || '',
            material: data.material || '',
            color: data.color || '',
            dimensions: data.dimensions || '',
            weight: data.weight || '',
            packagingUnit: data.packagingUnit || '',
            origin: data.origin || '',
            certifications: data.certifications || '',
            shippingFrom: data.shippingFrom || 'Seoul, South Korea'
          });
        } else {
          alert('Product not found');
          navigate('/seller/products');
        }
      } catch (error) {
        console.error('Error fetching product:', error);
        alert('Failed to load product');
        navigate('/seller/products');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id, navigate]);

  // const handleMainCategoryChange = (e) => {
  //   const main = e.target.value;
  //   setProduct(prev => ({ ...prev, mainCategory: main, subCategory: '' }));
  // };

  const addMOQPriceTier = () => {
    const lastTier = product.moqPrices[product.moqPrices.length - 1];
    setProduct(prev => ({
      ...prev,
      moqPrices: [...prev.moqPrices, { 
        minQty: lastTier.maxQty + 1, 
        maxQty: lastTier.maxQty + 50, 
        unitPrice: Math.max(1, lastTier.unitPrice - 1) 
      }]
    }));
  };

  const updateMOQPriceTier = (index, field, value) => {
    const newValue = field === 'unitPrice' ? parseFloat(value) : parseInt(value);
    if (isNaN(newValue) || newValue < 0) return;
    setProduct(prev => {
      const updated = [...prev.moqPrices];
      updated[index] = { ...updated[index], [field]: newValue };
      return { ...prev, moqPrices: updated };
    });
  };

  const removeMOQPriceTier = (index) => {
    if (product.moqPrices.length > 1) {
      setProduct(prev => ({
        ...prev,
        moqPrices: prev.moqPrices.filter((_, i) => i !== index)
      }));
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    const urls = files.map(file => URL.createObjectURL(file));
    setNewImages(prev => [...prev, ...urls]);
    setImageFiles(prev => [...prev, ...files]);
  };

  const removeImage = (index, isExisting = false) => {
    if (isExisting) {
      setProduct(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
    } else {
      setNewImages(prev => prev.filter((_, i) => i !== index));
      setImageFiles(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleSpecChange = (field, value) => {
    setProduct(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationError('');

    // ✅ 수정 가능 항목만 검사 (SKU, 카테고리, origin 등은 제외)
    const requiredEditableFields = [
      'name', 'description', 'material', 'color', 'dimensions', 'weight',
      'packagingUnit', 'shippingFrom', 'origin' 
    ];
    for (const field of requiredEditableFields) {
      if (!product[field]?.toString().trim()) {
        setValidationError(`${field.charAt(0).toUpperCase() + field.slice(1)} is required.`);
        return;
      }
    }

    if (product.images.length + newImages.length === 0) {
      setValidationError('At least one product image is required.');
      return;
    }

    try {
      setSaving(true);
      let imageURLs = [...product.images];
      if (imageFiles.length > 0) {
        const uploadPromises = imageFiles.map((file) => {
          const randomString = Array.from(crypto.getRandomValues(new Uint8Array(16))).map(b => b.toString(16).padStart(2, '0')).join('');
          const fileName = `${Date.now()}_${randomString}_${file.name.replace(/\s+/g, '_')}`;
          const storageRef = ref(storage, `products/${product.sellerId}/${fileName}`);
          return uploadBytes(storageRef, file).then(snapshot => getDownloadURL(snapshot.ref));
        });
        const newImageURLs = await Promise.all(uploadPromises);
        imageURLs = [...imageURLs, ...newImageURLs];
      }

      const normalizedMoqPrices = normalizeAndValidateTiers(product.moqPrices);

      await updateDoc(doc(db, 'products', id), {
        ...product,
        category: `${product.mainCategory} > ${product.subCategory}`,
        images: imageURLs,
        moqPrices: normalizedMoqPrices,
        updatedAt: new Date()
      });

      newImages.forEach(url => URL.revokeObjectURL(url));
      alert('Product updated successfully!');
      navigate('/seller/products');
    } catch (error) {
      console.error('Update failed:', error);
      setValidationError(error.message || 'Failed to update product');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto py-12 text-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
        <p className="mt-4 text-gray-600">Loading product...</p>
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="max-w-4xl mx-auto py-8">
      <div className="flex items-center gap-2 mb-6">
        <button onClick={() => navigate('/seller/products')} className="text-blue-600 hover:text-blue-800 flex items-center gap-2">
          ← Back to Products
        </button>
      </div>
      
      <div className="bg-white rounded-xl shadow-md p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Edit Product</h1>
        
        {validationError && (
          <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg border border-red-200">
            <strong>Validation Error:</strong> {validationError}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* SKU (수정 불가) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">SKU *</label>
            <input
              type="text"
              value={product.sku}
              disabled
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500 cursor-not-allowed"
            />
            <p className="text-xs text-gray-500 mt-1">Cannot be modified after approval.</p>
          </div>

          {/* 카테고리 (수정 불가) */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Main Category *</label>
              <select
                value={product.mainCategory}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500 cursor-not-allowed"
              >
                <option>{product.mainCategory}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sub Category *</label>
              <select
                value={product.subCategory}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500 cursor-not-allowed"
              >
                <option>{product.subCategory}</option>
              </select>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
            <textarea
              required
              rows={4}
              value={product.description}
              onChange={(e) => setProduct(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* 이미지 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Product Images</label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input type="file" multiple accept="image/*" onChange={handleImageUpload} className="hidden" id="image-upload" />
              <label htmlFor="image-upload" className="mt-2 bg-blue-600 text-white px-4 py-2 rounded-lg cursor-pointer hover:bg-blue-700">
                Add Images
              </label>
            </div>
            
            {product.images?.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Current Images</h3>
                <div className="grid grid-cols-3 gap-2">
                  {product.images.map((url, index) => (
                    <div key={`existing-${index}`} className="relative">
                      <img src={url} alt={`product ${index}`} className="w-full h-20 object-cover rounded" />
                      <button type="button" onClick={() => removeImage(index, true)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center">×</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {newImages.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">New Images</h3>
                <div className="grid grid-cols-3 gap-2">
                  {newImages.map((url, index) => (
                    <div key={`new-${index}`} className="relative">
                      <img src={url} alt={`new ${index}`} className="w-full h-20 object-cover rounded" />
                      <button type="button" onClick={() => removeImage(index)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center">×</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* 스펙 (origin, certifications는 수정 불가) */}
          <div className="border border-gray-200 rounded-xl p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Product Specifications</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Material *</label>
                <input
                  type="text"
                  required
                  value={product.material}
                  onChange={(e) => handleSpecChange('material', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Color *</label>
                <input
                  type="text"
                  required
                  value={product.color}
                  onChange={(e) => handleSpecChange('color', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Dimensions (mm) *</label>
                <input
                  type="text"
                  required
                  value={product.dimensions}
                  onChange={(e) => handleSpecChange('dimensions', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Weight (kg) *</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={product.weight}
                  onChange={(e) => handleSpecChange('weight', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Packaging Units/Box *</label>
                <input
                  type="number"
                  required
                  value={product.packagingUnit}
                  onChange={(e) => handleSpecChange('packagingUnit', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country of Origin *</label>
                <input
                  type="text"
                  value={product.origin}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500 cursor-not-allowed"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Certifications</label>
                <input
                  type="text"
                  value={product.certifications}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500 cursor-not-allowed"
                />
              </div>
            </div>
          </div>

          {/* 배송 정보 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ships From *</label>
            <input
              type="text"
              required
              value={product.shippingFrom}
              onChange={(e) => handleSpecChange('shippingFrom', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* MOQ */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <label className="block text-sm font-medium text-gray-700">MOQ Pricing Tiers *</label>
              <button type="button" onClick={addMOQPriceTier} className="bg-blue-600 text-white px-3 py-1 rounded-lg text-sm hover:bg-blue-700">
                Add Tier
              </button>
            </div>
            <div className="space-y-3">
              {product.moqPrices.map((tier, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1 grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Min Qty</label>
                      <input type="number" min="1" value={tier.minQty} onChange={(e) => updateMOQPriceTier(index, 'minQty', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Max Qty</label>
                      <input type="number" min={tier.minQty} value={tier.maxQty} onChange={(e) => updateMOQPriceTier(index, 'maxQty', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Unit Price ($)</label>
                      <input type="number" step="0.01" min="0.01" value={tier.unitPrice} onChange={(e) => updateMOQPriceTier(index, 'unitPrice', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
                    </div>
                  </div>
                  {product.moqPrices.length > 1 && (
                    <button type="button" onClick={() => removeMOQPriceTier(index)} className="text-red-500 hover:text-red-700 p-1">×</button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Stock Quantity *</label>
            <input
              type="number"
              min="1"
              required
              value={product.stock}
              onChange={(e) => setProduct(prev => ({ ...prev, stock: parseInt(e.target.value) || 1 }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex gap-4">
            <button type="submit" disabled={saving} className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed">
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <button type="button" onClick={() => navigate('/seller/products')} className="flex-1 bg-gray-200 text-gray-800 py-3 px-4 rounded-lg hover:bg-gray-300">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProductPage;